import { useCallback, useState } from 'react';
import {
  Container,
  Paper,
  Stack,
  Typography,
  Alert,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import StudentForm from '../components/StudentForm';
import StudentSearchAutocomplete from '../components/StudentSearchAutocomplete';
import AuthorizedPersonsDisplay from '../components/AuthorizedPersonsDisplay';
import { RegisteredStudent } from '../api/staffStudents';
import { checkInStudent } from '../api/students';

type CheckInMode = 'search' | 'manual';

export default function CheckInPage() {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showSuccess, setShowSuccess] = useState(false);
  const [mode, setMode] = useState<CheckInMode>('search');
  const [selectedStudent, setSelectedStudent] = useState<RegisteredStudent | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  const handleSuccess = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['students'] });
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);

    // Reset selected student in search mode
    if (mode === 'search') {
      setSelectedStudent(null);
    }
  }, [queryClient, mode]);

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: CheckInMode | null) => {
    if (newMode !== null) {
      setMode(newMode);
      setSelectedStudent(null); // Clear selection when switching modes
    }
  };

  const handleQuickCheckIn = async () => {
    if (!selectedStudent) return;

    setIsCheckingIn(true);
    try {
      await checkInStudent({
        first_name: selectedStudent.first_name,
        last_name: selectedStudent.last_name,
        grade: selectedStudent.grade,
        pickup_type: selectedStudent.pickup_type,
        registered_student_id: selectedStudent.id,
      });
      toast.success('Student checked in successfully!');
      await handleSuccess();
    } catch (error) {
      console.error('[CheckInPage] Quick check-in failed', error);
      toast.error('Failed to check in student');
    } finally {
      setIsCheckingIn(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 5 } }}>
      <Stack spacing={3}>
        <div>
          <Typography
            variant="h4"
            fontWeight={600}
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Check-in Student
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Search for registered students or manually enter walk-in information. Lists update instantly for everyone watching the dismissal board.
          </Typography>
        </div>

        <Collapse in={showSuccess}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Student checked in successfully! They now appear on the Dismissal Board.
          </Alert>
        </Collapse>

        {/* Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            aria-label="check-in mode"
            size={isMobile ? 'medium' : 'large'}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            fullWidth={isMobile}
            sx={{
              '& .MuiToggleButton-root': {
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          >
            <ToggleButton value="search" aria-label="search mode">
              <SearchIcon sx={{ mr: 1 }} />
              {isMobile ? 'Search Students' : 'Search Registered Students'}
            </ToggleButton>
            <ToggleButton value="manual" aria-label="manual mode">
              <EditIcon sx={{ mr: 1 }} />
              {isMobile ? 'Walk-ins' : 'Manual Entry (Walk-ins)'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search Mode */}
        {mode === 'search' && (
          <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
              <StudentSearchAutocomplete
                onSelect={setSelectedStudent}
                disabled={isCheckingIn}
              />

              {selectedStudent && (
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                        Student Information
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedStudent.first_name} {selectedStudent.last_name}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Grade
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedStudent.grade}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Family
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedStudent.family.family_name}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pickup Type
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedStudent.pickup_type === 'car' ? 'Car' : 'Walker'}
                          </Typography>
                        </Box>
                        {selectedStudent.medical_notes && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600}>Medical Notes:</Typography>
                            <Typography variant="body2">{selectedStudent.medical_notes}</Typography>
                          </Alert>
                        )}
                      </Stack>

                      <Box sx={{ mt: 3 }}>
                        <LoadingButton
                          variant="contained"
                          size="large"
                          fullWidth={isMobile}
                          loading={isCheckingIn}
                          onClick={handleQuickCheckIn}
                          sx={{
                            minHeight: { xs: 48, sm: 42 },
                            fontSize: { xs: '1rem', sm: '0.875rem' },
                            ...(isMobile ? {} : { float: 'right' })
                          }}
                        >
                          Check In Student
                        </LoadingButton>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <AuthorizedPersonsDisplay studentId={selectedStudent.id} />
                  </Grid>
                </Grid>
              )}
            </Stack>
          </Paper>
        )}

        {/* Manual Entry Mode */}
        {mode === 'manual' && (
          <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
            <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Use this mode for walk-in students who are not pre-registered in the system.
            </Alert>
            <StudentForm onSuccess={handleSuccess} />
          </Paper>
        )}

        <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          After checking in students here, go to the <strong>Dismissal Board</strong> to mark them as dismissed when they're picked up. All updates happen in real-time for all users.
        </Alert>
      </Stack>
    </Container>
  );
}
